import type { FC, ReactNode } from 'react'

interface FloorPlanProps {
    className?: string
}

// A cabin plan in champagne line art, drawn in the same coordinates as the jet artwork
// (1856 x 2304) so it lands exactly on the fuselage when the jet turns to a blueprint.
const rect = (x: number, y: number, width: number, height: number, radius = 6): string => {
    const r = Math.min(radius, width / 2, height / 2)
    return [
        `M ${x + r} ${y}`,
        `H ${x + width - r}`,
        `Q ${x + width} ${y} ${x + width} ${y + r}`,
        `V ${y + height - r}`,
        `Q ${x + width} ${y + height} ${x + width - r} ${y + height}`,
        `H ${x + r}`,
        `Q ${x} ${y + height} ${x} ${y + height - r}`,
        `V ${y + r}`,
        `Q ${x} ${y} ${x + r} ${y}`,
        'Z'
    ].join(' ')
}

const circle = (cx: number, cy: number, r: number): string => (
    `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`
)

const line = (x1: number, y1: number, x2: number, y2: number): string => `M ${x1} ${y1} L ${x2} ${y2}`

// A seat seen from above, with its backrest on the side it faces away from
const seat = (x: number, y: number, facing: 'up' | 'down'): string[] => {
    const width = 38
    const height = 40
    const back = facing === 'up' ? line(x + 4, y + height - 7, x + width - 4, y + height - 7) : line(x + 4, y + 7, x + width - 4, y + 7)
    return [rect(x, y, width, height, 7), back]
}

const FUSELAGE = [
    'M 928 290',
    'C 962 330 996 404 1002 522',
    'L 1002 1340',
    'C 1000 1600 976 1850 944 1996',
    'Q 928 2024 912 1996',
    'C 880 1850 856 1600 854 1340',
    'L 854 522',
    'C 860 404 894 330 928 290',
    'Z'
].join(' ')

const PATHS: string[] = [
    FUSELAGE,
    'M 884 424 Q 928 396 972 424',
    line(862, 482, 994, 482),

    // Forward galley, crew seat and lavatory
    rect(950, 494, 38, 78),
    line(958, 512, 980, 512),
    line(958, 532, 980, 532),
    rect(870, 496, 30, 28),
    rect(868, 536, 38, 42),
    circle(887, 557, 9),
    line(862, 592, 906, 592),
    line(950, 592, 994, 592),

    // Club seating for four
    ...seat(868, 608, 'down'),
    ...seat(950, 608, 'down'),
    rect(872, 658, 30, 30, 4),
    rect(954, 658, 30, 30, 4),
    ...seat(868, 700, 'up'),
    ...seat(950, 700, 'up'),

    // Dining for four
    ...seat(868, 772, 'down'),
    rect(870, 820, 34, 44, 4),
    ...seat(868, 872, 'up'),
    rect(958, 780, 28, 124, 5),
    line(862, 930, 906, 930),
    line(950, 930, 994, 930),

    // Divan and a facing pair
    rect(868, 948, 28, 162, 8),
    line(868, 1002, 896, 1002),
    line(868, 1056, 896, 1056),
    ...seat(950, 950, 'down'),
    rect(954, 998, 30, 32, 4),
    ...seat(950, 1040, 'up'),

    // Stateroom
    line(862, 1130, 902, 1130),
    line(946, 1130, 994, 1130),
    rect(872, 1146, 94, 98, 8),
    line(872, 1172, 966, 1172),
    rect(972, 1150, 16, 40, 3),
    line(862, 1258, 994, 1258),

    // Aft lavatory and baggage
    circle(900, 1292, 13),
    rect(946, 1272, 42, 40, 5),
    circle(967, 1292, 8),
    rect(872, 1330, 112, 84, 6)
]

const FloorPlan: FC<FloorPlanProps> = ({ className }): ReactNode => {
    return (
        <svg
            data-floor-plan
            viewBox='0 0 1856 2304'
            aria-hidden='true'
            className={className}
            fill='none'
        >
            {PATHS.map((d, index) => (
                <path
                    key={index}
                    data-plan-stroke
                    d={d}
                    pathLength={1}
                    stroke='currentColor'
                    strokeWidth={index === 0 ? 4 : 3}
                    strokeLinejoin='round'
                    style={{ strokeDasharray: 1, strokeDashoffset: 0 }}
                />
            ))}
        </svg>
    )
}

export default FloorPlan
